// Fiók detail + szerkesztő EGY oldalon (full-page) — az ügyfél-oldal (`clients.$id.tsx`) mintája.
//
// MIÉRT EGY OLDAL: korábban a fiók KÉT route volt — egy read-only detail (`Card`-ok rácsa) és egy
// külön `/accounts/$id/edit` szerkesztő. Ez háromszor is megbosszulta magát: (a) minden apró javításhoz oda-vissza
// kellett navigálni, (b) a két lap MÁS elrendezésben mutatta ugyanazt (a felhasználónak kétszer kellett megtanulnia,
// hol mi van), (c) a mezők egy része a detailen egyáltalán nem látszott (a legacy slug-ok, az üres mezők). A
// szerkeszthető lap ezt mind megoldja: EGY hasáb, `FormSection`-ök, ragadós szekció-navigáció, és jog nélkül
// (accounts.manage híján) ugyanez a lap view-only (disabled mezők, mentés-gomb nélkül).
//
// Mentés: PATCH If-Match/OCC + optimista detail-frissítés; 422 → mező-hiba, 409 → „valaki módosította" toast +
// reload, 403 → toast. Törlés: `DangerZone` → ConfirmDialog → soft-delete → vissza a listára. A kötések
// (kapcsolattartó / ügyféladminisztrátor) a saját szekcióikban, sheeten írnak — azonnal, nem ezzel a formmal.
import { IdentificationIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AppShell } from '@/components/shared/app-shell';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DangerZone } from '@/components/shared/danger-zone';
import { EmptyState } from '@/components/shared/empty-state';
import { FormPage } from '@/components/shared/form-page';
import { SectionNav, type SectionNavItem } from '@/components/shared/section-nav';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { accountKeys } from '@/features/accounts/api';
import { AccountForm } from '@/features/accounts/components/account-form';
import { AccountPeopleSection } from '@/features/accounts/components/account-people-section';
import { AccountServicesSection } from '@/features/accounts/components/account-services-section';
import {
  ACCOUNT_SELECT_NONE,
  EMPTY_ACCOUNT_FORM,
  accountFormSchemaWithRules,
  accountFormToBody,
  accountToFormValues,
  buildAccountErrorMap,
  type AccountFormValues,
} from '@/features/accounts/form';
import { DeactivationBillingNotice } from '@/components/shared/deactivation-billing-notice';
import { serviceAssignmentKeys } from '@/features/service-assignments/api';
import { ACCOUNT_SECTION } from '@/features/accounts/sections';
import { api } from '@/lib/api';
import { requireAuth } from '@/lib/auth';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { usePermissions } from '@/lib/use-permissions';
import { isForbiddenStatus } from '@/lib/error-status';

export const Route = createFileRoute('/accounts/$id')({
  beforeLoad: () => requireAuth(),
  component: AccountDetailPage,
});

type DetailPage = Awaited<ReturnType<typeof api.accountGet.query>>;

/** Szöveges mező üres → null (a szerver-oldali normalizálás tükre az optimista detail-íráshoz). */
const txt = (v: string): string | null => (v.trim() === '' ? null : v);
/** Select-mező NONE-sentinel/üres → null. */
const sel = (v: string): string | null => (v === ACCOUNT_SELECT_NONE || v.trim() === '' ? null : v);

function AccountDetailPage(): JSX.Element {
  const { id } = Route.useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can, isLoading: permsLoading } = usePermissions();
  const canView = can('accounts.view');
  const canManage = can('accounts.manage');
  // A KÖTÉS-műveletek SAJÁT jogai (a `accounts.manage` a fiók-ADATOT őrzi, nem a kötéseket): fajtánként és
  // műveletenként külön kulcs, pontosan úgy, ahogy a BFF is dönt (`AFFILIATION_PERMISSION`).
  const affiliationRights = {
    contact: { assign: can('contacts.assign'), remove: can('contacts.remove') },
    admin: { assign: can('admins.assign'), remove: can('admins.remove') },
  };
  const canDelete = can('accounts.delete');
  // A partner-portál hozzáférés szekció külön jog-kapun áll (a fiók-szerkesztéstől FÜGGETLENÜL).
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Page-gate: accounts.view nélkül jog-hiba (a BFF is 403-at ad). A query enélkül is lefut (szerver-oldalon
  // védett), de az ÜZENETET a jog dönti el. (A ts-rest v4 useQuery a jelen tanstack-v5 alatt nem vesz
  // `enabled`-et, ezért a page-gate az UI-ban dől el.)
  const gated = !permsLoading && !canView;

  const detailKey = accountKeys.detail(id);
  const detailQuery = api.accountGet.useQuery(detailKey, { params: { id } });
  const account = detailQuery.data?.status === 200 ? detailQuery.data.body : undefined;
  const version = account?.version;
  const errStatus = detailQuery.error?.status;
  const isForbidden = gated || isForbiddenStatus(errStatus);

  // Slug → hivatalos név feloldás (típus/pms/régió/…) — a listával azonos reference-data forrás; best-effort.
  const refsQ = api.accountRefs.useQuery(['account-refs'], {});
  const refs = refsQ.data?.status === 200 ? refsQ.data.body : undefined;

  /**
   * A fiók hozzárendelései — CSAK az inaktiválás-figyelmeztetéshez. UGYANAZ a query-kulcs, amit a
   * „Szolgáltatások" szekció használ, tehát a TanStack Query összevonja: nincs plusz hálózati kör.
   */
  const assignmentsQuery = api.serviceAssignments.useQuery(
    [...serviceAssignmentKeys.lists(), 'by-account', id],
    { query: { accountId: id, limit: 200 } },
  );
  const assignmentsForNotice =
    assignmentsQuery.data?.status === 200 ? assignmentsQuery.data.body.assignments : [];

  const form = useForm<AccountFormValues>({
    resolver: zodResolver(accountFormSchemaWithRules, {
      errorMap: buildAccountErrorMap(t),
    }) as Resolver<AccountFormValues>,
    mode: 'onTouched', // validáció már mentés előtt: a mezőből kilépve (blur), utána élőben
    defaultValues: EMPTY_ACCOUNT_FORM,
  });

  const { reset } = form;
  const isDirty = form.formState.isDirty;
  useEffect(() => {
    // CSAK amíg a felhasználó nem piszkálta a formot — egy háttér-refetch ne írja felül a szerkesztést.
    if (account && !isDirty) reset(accountToFormValues(account));
  }, [account, isDirty, reset]);

  const updateMutation = useEntityMutation<AccountFormValues, DetailPage>({
    mutationFn: (values) =>
      api.accountUpdate.mutation({
        params: { id },
        body: accountFormToBody(values),
        headers: { 'if-match': version !== undefined ? String(version) : undefined },
      }),
    invalidateKeys: [detailKey, accountKeys.lists()],
    optimistic: {
      queryKey: detailKey,
      update: (old) => {
        if (old?.status !== 200) return old;
        const fv = form.getValues();
        return {
          ...old,
          body: {
            ...old.body,
            name: fv.name,
            status: fv.status,
            website: txt(fv.website),
            category: sel(fv.category),
            countryCode: txt(fv.countryCode),
            countryName: txt(fv.countryName),
            zip: txt(fv.zip),
            city: txt(fv.city),
            address: txt(fv.address),
            note: txt(fv.note),
            bookingEngine: sel(fv.bookingEngine),
            pms: sel(fv.pms),
            channelManager: sel(fv.channelManager),
            emailWebHosting: sel(fv.emailWebHosting),
            region: sel(fv.region),
            roomCount: fv.roomCount.trim() === '' ? null : Number(fv.roomCount.trim()),
            types: fv.types,
            billingProfileId: sel(fv.billingProfileId),
            asanaName: txt(fv.asanaName),
            integrations: fv.integrations.map((i) => ({
              id: i.id,
              system: i.system,
              externalId: i.externalId.trim(),
              note: txt(i.note.trim()),
            })),
          },
        };
      },
    },
    onFieldErrors: (field, messages) =>
      form.setError(field as keyof AccountFormValues, { message: messages.join(', ') }),
    onConflict: () => void detailQuery.refetch(),
    messages: {
      success: t('toast:accounts.saved'),
      conflict: t('toast:accounts.conflict'),
      forbidden: t('toast:accounts.forbidden'),
      validation: t('toast:accounts.validation'),
      error: t('toast:accounts.error'),
    },
  });

  const removeMutation = useEntityMutation<{ id: string }, unknown>({
    mutationFn: ({ id: accountId }) => api.accountDelete.mutation({ params: { id: accountId } }),
    invalidateKeys: [accountKeys.lists()],
    messages: {
      success: t('toast:accounts.deleted'),
      conflict: t('toast:accounts.conflict'),
      forbidden: t('toast:accounts.forbidden'),
      validation: t('toast:accounts.validation'),
      error: t('toast:accounts.error'),
    },
  });

  function onSubmit(values: AccountFormValues): void {
    // Siker után MARADUNK a lapon (nincs navigáció): a szerkesztés és az olvasás ugyanaz a nézet, a
    // visszajelzést a toast + az optimista frissítés adja.
    // A domain-sorok azonosítóját a SZERVER adja: siker után a form a szerver-válaszból töltődik újra (különben
    // az új sor azonosító nélkül maradna, és egy második mentés újra felvenné).
    updateMutation.mutate(values, {
      onSuccess: (res) => {
        if (res.status === 200)
          reset(accountToFormValues(res.body as Parameters<typeof accountToFormValues>[0]));
      },
    });
  }

  function confirmDelete(): void {
    removeMutation.mutate(
      { id },
      {
        // onSuccess csak sikeren fut (a hook a 4xx-et dobja) → nincs szükség státusz-ellenőrzésre.
        onSuccess: () => {
          setConfirmOpen(false);
          void queryClient.removeQueries({ queryKey: detailKey });
          void navigate({ to: '/accounts' });
        },
      },
    );
  }

  /**
   * A rail elemei — CSAK a ténylegesen renderelt szekciók. Az Integrációk szekció írás-joghoz kötött, a
   * Veszélyzóna törlés-joghoz; egy fix lista olyan célra mutatna, ami nincs a lapon (a kattintás némán nem
   * csinálna semmit). A sorrend a DOM-sorrendé (`features/accounts/sections.ts`).
   */
  const navItems: SectionNavItem[] = [
    { id: ACCOUNT_SECTION.basics, label: t('accounts.sections.basics') },
    { id: ACCOUNT_SECTION.other, label: t('accounts.sections.other') },
    { id: ACCOUNT_SECTION.domains, label: t('accounts.domains.title') },
    { id: ACCOUNT_SECTION.services, label: t('accounts.sections.services') },
    { id: ACCOUNT_SECTION.billing, label: t('accounts.sections.billing') },
    ...(canManage
      ? [{ id: ACCOUNT_SECTION.integrations, label: t('accounts.sections.integrations') }]
      : []),
    { id: ACCOUNT_SECTION.contacts, label: t('accounts.sections.contacts') },
    // A portál-hozzáférés SZEKCIÓ csak jog birtokában létezik — a nav-elem is csak akkor (különben a
    // ragadós rail egy nem létező horgonyra ugrana).
    { id: ACCOUNT_SECTION.admins, label: t('accounts.sections.admins') },
    ...(canDelete ? [{ id: ACCOUNT_SECTION.danger, label: t('common.dangerZone') }] : []),
  ];

  if (!gated && detailQuery.isLoading) {
    return (
      <AppShell>
        <FormPage>
          <div className="space-y-4">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-64 w-full" />
          </div>
        </FormPage>
      </AppShell>
    );
  }

  if (isForbidden || !account) {
    return (
      <AppShell>
        <FormPage>
          <EmptyState
            title={isForbidden ? t('accounts.forbidden') : t('accounts.notFound')}
            description={isForbidden ? t('accounts.forbiddenDesc') : t('accounts.notFoundDesc')}
            action={
              <Button variant="outline" onClick={() => void navigate({ to: '/accounts' })}>
                {t('accounts.back')}
              </Button>
            }
          />
        </FormPage>
      </AppShell>
    );
  }

  return (
    // A cím a TOPBAR morzsájában („Fiókok / <fiók>") — az oldal törzsében nincs h1, és alcím sincs: a morzsa
    // már megmondja, hol vagy és mi ez (design.md — az alcím a LISTA-oldalak sajátja).
    <AppShell parent={{ label: t('nav.accounts'), to: '/accounts' }} title={account.name}>
      <FormPage aside={<SectionNav items={navItems} ariaLabel={t('accounts.sectionNavLabel')} />}>
        <div className="space-y-8">
          <AccountForm
            form={form}
            mode="edit"
            refs={refs}
            billingProfileOptions={account.billingProfileOptions}
            disabled={!canManage}
            pending={updateMutation.isPending}
            onSubmit={onSubmit}
            t={t}
            servicesSlot={
              <AccountServicesSection accountId={account.id} clientId={account.clientId} t={t} />
            }
            // INAKTIVÁLÁS: mi lesz a fiók nyitott számlázás-soraival (l. `DeactivationBillingNotice`).
            statusNotice={
              form.watch('status') === 'inactive' && account.status !== 'inactive' ? (
                <DeactivationBillingNotice rows={assignmentsForNotice} t={t} />
              ) : null
            }
          >
            {/* KAPCSOLATTARTÓK · ÜGYFÉLADMINISZTRÁTOROK — a kért sorrend szerint a form UTÁN. Külön írnak
                (sheet + azonnali mentés), tehát a form mentés-gombja nem érinti őket. */}
            <AccountPeopleSection
              accountId={id}
              kind="contact"
              sectionId={ACCOUNT_SECTION.contacts}
              title={t('accounts.sections.contacts')}
              emptyIcon={UserGroupIcon}
              people={account.contacts}
              affiliationDetails={account.affiliationDetails}
              canAssign={affiliationRights.contact.assign}
              canRemove={affiliationRights.contact.remove}
              t={t}
              onChanged={() => void detailQuery.refetch()}
            />
            <AccountPeopleSection
              accountId={id}
              kind="admin"
              sectionId={ACCOUNT_SECTION.admins}
              title={t('accounts.sections.admins')}
              emptyIcon={IdentificationIcon}
              people={account.admins}
              affiliationDetails={account.affiliationDetails}
              canAssign={affiliationRights.admin.assign}
              canRemove={affiliationRights.admin.remove}
              t={t}
              onChanged={() => void detailQuery.refetch()}
            />
          </AccountForm>

          {/* VESZÉLYZÓNA — a lap legalján, keretes/veszély-tónusú dobozban (a törlés korábban a fejlécben, a
              cím mellett lapult: ránézésre ugyanolyan volt, mint a szerkesztés). */}
          {canDelete && (
            <DangerZone
              id={ACCOUNT_SECTION.danger}
              title={t('common.dangerZone')}
              actionTitle={t('accounts.dangerTitle')}
              description={t('accounts.dangerDesc')}
              actionLabel={t('accounts.delete')}
              onAction={() => setConfirmOpen(true)}
            />
          )}
        </div>
      </FormPage>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('accounts.deleteTitle')}
        description={t('accounts.deleteDesc', { name: account.name })}
        confirmLabel={t('accounts.delete')}
        cancelLabel={t('accounts.cancel')}
        pendingLabel={t('accounts.deleting')}
        destructive
        pending={removeMutation.isPending}
        onConfirm={confirmDelete}
      />
    </AppShell>
  );
}
